import React from "react"
import { TableHeaderColumn, TableRow, TableHeader, TableRowColumn, TableBody } from "material-ui"
import Table from "../../components/common/Table"
import strings from "../../localization/strings"
import ImageLoader from "../../components/misc/ImageLoader"
import ColumnSelector from "../../components/common/columnSelector/ColumnSelector"
import AccountStore from "../../stores/AccountStore"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import { IEmployeeReportColumns, ITableColumnSelect, IUIUser } from "../../models"
import { red500 } from "material-ui/styles/colors"
import * as userHelper from "../../helpers/userHelper"
import ExternalLink from "../../components/common/externalLink/ExternalLink"
import * as payGroupAndTypeHelper from "../../helpers/payGroupAndTypeHelper"
import MultiSupervisorChips from "../../components/supervisor/MultiSupervisorChips"
import { checkJobFieldAvailable } from "../../helpers/jobHelper"
interface IProps {
    readonly: boolean
    users: IUIUser[]
    selectedUsers: IUIUser[]
    total: number
    offset: number
    limit: number
    limitOptions: number[]
    employeeColumns: IEmployeeReportColumns
    tableColumnSelect: ITableColumnSelect[]
    handleSelect(selectedUsers: IUIUser[]): void
    onRowClick(userId: string): void
    onPageClick(offset: number): void
    onLimitChange(limit: number): void
    handleChangeTableColumns(columns: ITableColumnSelect[]): void
}

const EmployeeTable: React.SFC<IProps> = (props: IProps) => {
    return (
        <div className="UserTable" style={{ position: "relative" }}>
            <Table
                readonly={props.readonly}
                allowClickRow
                unClickableCols={[0]}
                items={props.users}
                handleSelect={props.handleSelect}
                handleClickRow={user => props.onRowClick(user.Id)}
                total={props.total}
                offset={props.offset}
                limit={props.limit}
                limitOptions={props.limitOptions}
                onPageClick={props.onPageClick}
                onLimitChange={props.onLimitChange}
            >
                <TableHeader>
                    <TableRow className="ShortSelectBoxTableHeader">
                        <TableHeaderColumn style={{ width: 25, paddingLeft: 5, paddingRight: 5 }} />
                        <TableHeaderColumn style={styles.imageColumn} />
                        {props.employeeColumns.IncludeFirstName &&
                            <TableHeaderColumn className="TextOverflow" title={strings.First_Name} style={styles.columnHeader}>{strings.First_Name}</TableHeaderColumn>
                        }
                        {props.employeeColumns.IncludeLastName &&
                            <TableHeaderColumn className="TextOverflow" title={strings.Last_Name} style={styles.columnHeader}>{strings.Last_Name}</TableHeaderColumn>
                        }
                        {AccountStore.getExplicitEmployeeHierarchyAssignment() ?
                            props.employeeColumns.IncludeDefaultHierarchy &&
                            <TableHeaderColumn className="TextOverflow" title={AccountStore.getExplicitEmployeeHierarchyAssignment() ? strings.Default_Hierarchy : strings.Punch_Hierarchy} style={styles.columnHeader}>
                                {AccountStore.getExplicitEmployeeHierarchyAssignment() ? strings.Default_Hierarchy : strings.Punch_Hierarchy}
                            </TableHeaderColumn> :
                            props.employeeColumns.IncludePunchHierarchy &&
                            <TableHeaderColumn className="TextOverflow" title={AccountStore.getExplicitEmployeeHierarchyAssignment() ? strings.Default_Hierarchy : strings.Punch_Hierarchy} style={styles.columnHeader}>
                                {AccountStore.getExplicitEmployeeHierarchyAssignment() ? strings.Default_Hierarchy : strings.Punch_Hierarchy}
                            </TableHeaderColumn>
                        }
                        {!AccountStore.getExplicitEmployeeHierarchyAssignment() &&
                            props.employeeColumns.IncludeDefaultHierarchy &&
                            <TableHeaderColumn className="TextOverflow" title={strings.Default_Hierarchy} style={styles.columnHeader}>{strings.Default_Hierarchy}</TableHeaderColumn>
                        }
                        {!AccountStore.getExplicitEmployeeHierarchyAssignment() && AccountStore.getJobsEnabled() && [
                            props.employeeColumns.IncludeDefaultJob &&
                            <TableHeaderColumn className="TextOverflow" title={strings.Default_Job} style={styles.columnHeader} key="job">{checkJobFieldAvailable('singular') ? `Default ${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}` : strings.Default_Job}</TableHeaderColumn>,
                            AccountStore.getPhasesEnabled() && props.employeeColumns.IncludeDefaultPhase &&
                            <TableHeaderColumn className="TextOverflow" title={strings.Default_Phase} style={styles.columnHeader} key="phase">{strings.Default_Phase}</TableHeaderColumn>
                        ]}
                        {props.employeeColumns.IncludeEmployeeId &&
                            <TableHeaderColumn className="TextOverflow" title={strings.Employee_ID} style={styles.columnHeader}>{strings.Employee_ID}</TableHeaderColumn>
                        }
                        {props.employeeColumns.IncludeClockPin &&
                            <TableHeaderColumn className="TextOverflow" title={strings.Clock_PIN} style={styles.columnHeader}>{strings.Clock_PIN}</TableHeaderColumn>
                        }
                       {props.employeeColumns.IncludePositions && <TableHeaderColumn className="TextOverflow" title={strings.Position} style={styles.columnHeader}>{strings.Position}</TableHeaderColumn>}
                        {AccountStore.getPayrollEnabled() && props.employeeColumns.IncludePayrollHierarchy &&
                            <TableHeaderColumn className="TextOverflow" title={strings.Payroll_Hierarchy} style={styles.columnHeader}>{strings.Payroll_Hierarchy}</TableHeaderColumn>
                        }
                        {AccountStore.getPayrollEnabled() && props.employeeColumns.IncludePayrollPayGroup &&
                            <TableHeaderColumn className="TextOverflow" title={strings.Pay_Group} style={styles.columnHeader}>{strings.Pay_Group}</TableHeaderColumn>
                        }
                        {props.employeeColumns.IncludeAgency &&
                            <TableHeaderColumn className="TextOverflow" title={strings.Agency} style={styles.columnHeader}>{strings.Agency}</TableHeaderColumn>
                        }
                        {props.employeeColumns.IncludeExempt &&
                            <TableHeaderColumn className="TextOverflow" title={strings.Exempt} style={styles.columnHeader}>{strings.Exempt}</TableHeaderColumn>
                        }
                        {props.employeeColumns.IncludeSupervisors &&
                            <TableHeaderColumn className="TextOverflow" title={strings.Supervisors} style={{ ...styles.columnHeader, width: 150 }}>
                                {strings.Supervisors}
                            </TableHeaderColumn>
                        }
                        <TableHeaderColumn style={{ ...styles.columnHeader, width: 50, float: Object.keys(props.employeeColumns).filter(x => props.employeeColumns[x] === true).length === 0 ? "right" : "none" }}>
                            <ColumnSelector
                                columns={props.tableColumnSelect}
                                handleChange={props.handleChangeTableColumns}
                            />
                        </TableHeaderColumn>
                    </TableRow>
                </TableHeader>
                <TableBody showRowHover={true} deselectOnClickaway={false}>
                    {props.users.map((row, i) => {
                        let smallImage
                        if (row.ProfileImageS3Url || row.ProfileImageID) {
                            smallImage = <ImageLoader url={row.ProfileImageS3Url} fixedHeight={75} avatar={true} src={"/users/profileimage/" + row.ProfileImageID} key={`smallImg-${i}`} />
                        }

                        const punchPermissionHierarchyName = hierarchyHelper.getFullyQualifiedLevelNameById(row.DefaultHierarchyItemId)
                        const defaultPunchHierarchyName = hierarchyHelper.getFullyQualifiedLevelNameById(row.DefaultPunchHierarchyId)
                        const payrollHierarchyName = hierarchyHelper.getFullyQualifiedLevelNameById(row.PayrollHierarchyItemId)
                        const payrollPayGroup = payGroupAndTypeHelper.getPayGroupById(row.PayrollPayGroupId)
                        return (
                            <TableRow
                                data-intercom-target={i === 0 && "menu-Employees-openEmployeeDetails"}
                                key={row.Id}
                                className="ShortSelectBoxTableRow"
                                selected={props.selectedUsers && props.selectedUsers.find(e => e.Id === row.Id) ? true : false}
                            >
                                <TableRowColumn data-intercom-target={i === 0 && "menu-Employees-openEmployeeDetailsInNewTab"} style={{ width: 25, paddingLeft: 5, paddingRight: 5, paddingTop: 4 }}>
                                    <ExternalLink
                                        url={"/user-details/" + row.Id + "/" + userHelper.getValidSubPages()[0]}
                                    />
                                </TableRowColumn>
                                <TableRowColumn style={styles.imageColumn}>{smallImage}</TableRowColumn>
                                {props.employeeColumns.IncludeFirstName &&
                                    <TableRowColumn style={styles.columnValue}><span title={row.FirstName}>{row.FirstName}</span></TableRowColumn>
                                }
                                {props.employeeColumns.IncludeLastName &&
                                    <TableRowColumn style={styles.columnValue}><span title={row.LastName}>{row.LastName}</span></TableRowColumn>
                                }
                                {AccountStore.getExplicitEmployeeHierarchyAssignment() ?
                                    props.employeeColumns.IncludeDefaultHierarchy &&
                                    <TableRowColumn style={styles.columnValue}>
                                        <span
                                            title={punchPermissionHierarchyName.includes(strings.Hierarchy_Removed) ? null : hierarchyHelper.getFullPathLabel(row.DefaultHierarchyItemId)}
                                            style={{ color: punchPermissionHierarchyName.includes(strings.Hierarchy_Removed) && red500 }}
                                        >
                                            {punchPermissionHierarchyName.includes(strings.Hierarchy_Removed) ? strings.Hierarchy_Removed : punchPermissionHierarchyName}
                                        </span>
                                    </TableRowColumn>
                                    :
                                    props.employeeColumns.IncludePunchHierarchy &&
                                    <TableRowColumn style={styles.columnValue}>
                                        <span
                                            title={punchPermissionHierarchyName.includes(strings.Hierarchy_Removed) ? null : hierarchyHelper.getFullPathLabel(row.DefaultHierarchyItemId)}
                                            style={{ color: punchPermissionHierarchyName.includes(strings.Hierarchy_Removed) && red500 }}
                                        >
                                            {punchPermissionHierarchyName.includes(strings.Hierarchy_Removed) ? strings.Hierarchy_Removed : punchPermissionHierarchyName}
                                        </span>
                                    </TableRowColumn>
                                }
                                {!AccountStore.getExplicitEmployeeHierarchyAssignment() &&
                                    props.employeeColumns.IncludeDefaultHierarchy &&
                                    <TableRowColumn style={styles.columnValue}>
                                        <span
                                            title={defaultPunchHierarchyName.includes(strings.Hierarchy_Removed) ? null : hierarchyHelper.getFullPathLabel(row.DefaultPunchHierarchyId)}
                                            style={{ color: defaultPunchHierarchyName.includes(strings.Hierarchy_Removed) && red500 }}
                                        >
                                            {defaultPunchHierarchyName.includes(strings.Hierarchy_Removed) ? strings.Hierarchy_Removed : defaultPunchHierarchyName}
                                        </span>
                                    </TableRowColumn>
                                }
                                {!AccountStore.getExplicitEmployeeHierarchyAssignment() && AccountStore.getJobsEnabled() && [
                                    props.employeeColumns.IncludeDefaultJob &&
                                    <TableRowColumn style={styles.columnValue} key="job">
                                        <span title={row.DefaultJobName}>{row.DefaultJobName}</span>
                                    </TableRowColumn>,
                                    AccountStore.getPhasesEnabled() && props.employeeColumns.IncludeDefaultPhase &&
                                    <TableRowColumn style={styles.columnValue} key="phase">
                                        <span title={row.DefaultPhaseName}>{row.DefaultPhaseName}</span>
                                    </TableRowColumn>,
                                ]}
                                {props.employeeColumns.IncludeEmployeeId &&
                                    <TableRowColumn style={styles.columnValue}><span title={row.ExternalId}>{row.ExternalId}</span></TableRowColumn>
                                }
                                {props.employeeColumns.IncludeClockPin &&
                                    <TableRowColumn style={styles.columnValue}><span title={row.ClockPin}>{row.ClockPin}</span></TableRowColumn>
                                }
                                 {props.employeeColumns.IncludePositions && <TableRowColumn style={styles.columnValue} title={row.PositionNames ? row.PositionNames.join(", ") : ""}>
                                    {row.PositionNames && <span>{row.PositionNames.join(", ")}</span>}
                                </TableRowColumn>}
                                {AccountStore.getPayrollEnabled() && props.employeeColumns.IncludePayrollHierarchy && (
                                    <TableRowColumn style={styles.columnValue}>
                                        <span
                                            title={payrollHierarchyName.includes(strings.Hierarchy_Removed) ? null : payrollHierarchyName}
                                            style={{ color: payrollHierarchyName.includes(strings.Hierarchy_Removed) && red500 }}
                                        >
                                            {payrollHierarchyName.includes(strings.Hierarchy_Removed) ? strings.None_exist : payrollHierarchyName}
                                        </span>
                                    </TableRowColumn>
                                )}
                                {AccountStore.getPayrollEnabled() && props.employeeColumns.IncludePayrollPayGroup &&
                                    <TableRowColumn style={styles.columnValue}>
                                        <span style={{ color: !payrollPayGroup && red500 }}>
                                            {payrollPayGroup ? payrollPayGroup.Name : strings.invalid}
                                        </span>
                                    </TableRowColumn>
                                }
                                {props.employeeColumns.IncludeAgency &&
                                    <TableRowColumn style={styles.columnValue}><span>{row.Agency ? "True" : "False"}</span></TableRowColumn>
                                }
                                {props.employeeColumns.IncludeExempt &&
                                    <TableRowColumn style={styles.columnValue}><span>{row.FullTime ? "True" : "False"}</span></TableRowColumn>
                                }
                                {props.employeeColumns.IncludeSupervisors &&
                                    <TableRowColumn style={{ ...styles.columnValue, width: 150 }}>
                                        <MultiSupervisorChips
                                            readonly
                                            hideLabel
                                            maxCountShow={1}
                                            selectedSupervisorIds={row.SupervisorIds}
                                        />
                                    </TableRowColumn>
                                }
                                <TableRowColumn style={{ ...styles.columnValue, width: 50, float: Object.keys(props.employeeColumns).filter(x => props.employeeColumns[x] === true).length === 0 ? "right" : "none" }}></TableRowColumn>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </div>
    )
}

export default EmployeeTable

const styles = {
    columnValue: {
        textAlign: "left",
        cursor: "pointer",
        paddingLeft: 5,
        paddingRight: 5
    },
    columnHeader: {
        textAlign: "left",
        paddingLeft: 5,
        paddingRight: 5
    },
    imageColumn: {
        width: 40,
        textAlign: "center",
        paddingLeft: 5,
        paddingRight: 5,
    }
}
